import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import PropTypes from 'prop-types'

import { stringifyDate, formatDate, formatNumber } from '@atheneum/api/dist/lib'

import InfoBox from '@components/InfoBox'

import './AccountingAdjustmentsPreview.sass'

const feeValuesPropType = PropTypes.shape({
  value: PropTypes.number,
  currencyId: PropTypes.number,
  credits: PropTypes.number,
  deliveryDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  contractId: PropTypes.number
})

const fields = ['value', 'currencyId', 'deliveryDate', 'contractId']

class AccountingAdjustmentsPreview extends Component {
  static propTypes = {
    staticData: PropTypes.object,
    originalFeeValues: feeValuesPropType,
    newFeeValues: feeValuesPropType,
    contractOptions: PropTypes.array
  }

  getChangedFields = () => {
    const { originalFeeValues, newFeeValues } = this.props
    return fields.filter(field => {
      return Number(originalFeeValues[field]) !== Number(newFeeValues[field])
    })
  }

  calculateAdjustments = (originalFeeValues, newFeeValues) => {
    const adjustments = [
      // first 2 adjustments negate the original fee values
      {
        key: 'a1', // add keys for react rendering
        amount: originalFeeValues.value,
        currencyId: originalFeeValues.currencyId,
        documentDate: stringifyDate(originalFeeValues.deliveryDate)
      },
      {
        key: 'a2',
        amount: -1 * originalFeeValues.value,
        currencyId: originalFeeValues.currencyId,
        documentDate: stringifyDate(new Date())
      },
      // second 2 adjustments compensate for the changed fee values
      {
        key: 'a3',
        amount: -1 * newFeeValues.value,
        currencyId: newFeeValues.currencyId,
        documentDate: stringifyDate(newFeeValues.deliveryDate)
      },
      {
        key: 'a4',
        amount: newFeeValues.value,
        currencyId: newFeeValues.currencyId,
        documentDate: stringifyDate(new Date())
      }
    ]

    return adjustments
  }

  renderAdjustments = () => {
    const { originalFeeValues, newFeeValues, staticData } = this.props
    const { currencies } = staticData.names

    const adjustments = this.calculateAdjustments(
      originalFeeValues,
      newFeeValues
    )

    return (
      <div>
        <h3>Accounting adjustments</h3>
        <div className='m-b-xxl'>
          Adjustments will be created to compensate for the fee being changed
          after backdating rule is in effect.
        </div>
        {adjustments.map(adjustment => {
          return (
            <div key={adjustment.key} className='m-b-md'>
              <div>
                <b>Amount:</b> {formatNumber(adjustment.amount)}{' '}
                {currencies[adjustment.currencyId]}
              </div>
              <div>
                <b>Document Date:</b> {formatDate(adjustment.documentDate)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  renderChanges = () => {
    const {
      originalFeeValues,
      newFeeValues,
      contractOptions,
      staticData
    } = this.props
    const { currencies } = staticData.names

    const fieldTransforms = {
      value: {
        name: 'fee amount',
        value: formatNumber
      },
      currencyId: {
        name: 'currency',
        value: v => currencies[v]
      },
      contractId: {
        name: 'contract',
        value: v => {
          const contract = contractOptions.find(co => co.id === Number(v))
          return contract.label || contract.contractReference
        }
      },
      deliveryDate: {
        name: 'delivery date',
        value: formatDate
      }
    }

    const changedValues = this.getChangedFields()

    if (changedValues.length) {
      return (
        <div className='alertbox-wrapper'>
          <InfoBox theme='blue' className='m-b-lg' icon={null}>
            <h3>Alert</h3>
            {changedValues.map(val => {
              const fromValue = originalFeeValues[val]
              const toValue = newFeeValues[val]

              return (
                <div key={val}>
                  You intend to change the <b>{fieldTransforms[val].name}</b>{' '}
                  from <b>{fieldTransforms[val].value(fromValue)}</b> to{' '}
                  <b>{fieldTransforms[val].value(toValue)}</b>
                </div>
              )
            })}
          </InfoBox>
        </div>
      )
    }

    return null
  }

  render () {
    const changedValues = this.getChangedFields()

    if (changedValues.length) {
      return (
        <div className='accounting-adjustments-preview'>
          {this.renderChanges()}
          {this.renderAdjustments()}
        </div>
      )
    }

    return (
      <div className='accounting-adjustments-preview'>
        Fee unchanged, no adjustments needed.
      </div>
    )
  }
}

export default inject('staticData')(observer(AccountingAdjustmentsPreview))
